package org.teddapp.project.ui.finance

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Expense_list
import com.tedd.db.Expense_list_item
import com.tedd.db.Finance_account
import com.tedd.db.Finance_category
import com.tedd.db.Finance_transaction
import kotlinx.coroutines.launch
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.LocalUseFullScreenForm
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddLogo
import org.teddapp.project.ui.TeddSnackbarHost
import org.teddapp.project.db.repo.LoanTx
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.rememberTeddSnackbarState

// Types et aides partagés par toutes les mises en page de Finance.

/** Jour courant au format ISO, source unique pour la section. */
internal val TODAY_ISO: String get() = org.teddapp.project.db.TODAY_ISO
/** Premier et dernier jour du mois courant, bornes lexicographiques. */
internal val MONTH_START: String get() = TODAY_ISO.substring(0, 8) + "01"
internal val MONTH_END: String get() = TODAY_ISO.substring(0, 8) + "31"

internal enum class FinanceTab(val key: String, val label: String, val icon: String) {
    Transactions("transactions", "Transactions", "list"),
    Dashboard("dashboard", "Tableau de bord", "cards"),
    Lists("lists", "Listes de dépenses", "inbox"),
    Patrimoine("patrimoine", "Patrimoine", "home"),
    Simulation("simulation", "Simulation", "draw"),
}

internal enum class TxFilter(val key: String, val label: String) {
    All("all", "Tout"),
    Expense("expense", "Dépenses"),
    Income("income", "Entrées"),
    Transfer("transfer", "Transferts"),
}

/**
 * Plage temporelle pour le filtrage des transactions. Les bornes sont
 * calculées à partir de [TODAY_ISO] et restent purement lexicographiques
 * (YYYY-MM-DD se trie correctement).
 */
internal enum class TxPeriod(val key: String, val label: String) {
    All("all", "Tout"),
    Today("today", "Aujourd'hui"),
    Last7("7d", "7 jours"),
    Last30("30d", "30 jours"),
    ThisMonth("month", "Ce mois"),
    LastMonth("lastmonth", "Mois préc."),
}

/**
 * Bornes [from, to] ISO inclusive d'une période donnée par rapport à `today`.
 * Retourne `null` pour "tout" (pas de borne).
 */
internal fun periodBounds(period: TxPeriod, today: String): Pair<String, String>? {
    if (period == TxPeriod.All) return null
    if (period == TxPeriod.Today) return today to today
    if (period == TxPeriod.Last7)  return org.teddapp.project.ui.pomodoro.isoAddDays(today, -6) to today
    if (period == TxPeriod.Last30) return org.teddapp.project.ui.pomodoro.isoAddDays(today, -29) to today
    val y = today.substring(0, 4).toIntOrNull() ?: return null
    val m = today.substring(5, 7).toIntOrNull() ?: return null
    if (period == TxPeriod.ThisMonth) {
        val mm = m.toString().padStart(2, '0')
        return "$y-$mm-01" to "$y-$mm-31"
    }
    // LastMonth
    val pm = if (m == 1) 12 else m - 1
    val py = if (m == 1) y - 1 else y
    val pmm = pm.toString().padStart(2, '0')
    return "$py-$pmm-01" to "$py-$pmm-31"
}

internal fun parseFinHex(hex: String): Color {
    val clean = hex.trimStart('#')
    val rgb = clean.toLong(16)
    return Color(0xFF000000L or rgb)
}

internal fun txSignedAmountFor(tx: Finance_transaction, accountId: String?): Long {
    return when (tx.type) {
        "income" -> tx.amount
        "expense" -> -tx.amount
        "transfer" -> if (tx.account_id == accountId) -tx.amount else tx.amount
        LoanTx.LOAN_GIVEN, LoanTx.REPAYMENT_PAID -> -tx.amount
        else -> tx.amount
    }
}

internal fun dayLabel(iso: String): String = dayLabelFromIso(iso, TODAY_ISO)
internal fun dateLabel(iso: String): String {
    if (iso.length < 10) return iso
    val d = iso.substring(8, 10).trimStart('0').ifEmpty { "0" }
    val m = iso.substring(5, 7).toIntOrNull() ?: return iso
    val mois = listOf("jan","fév","mar","avr","mai","juin","juil","aoû","sep","oct","nov","déc")
    val y = iso.substring(2, 4)
    return "$d ${mois.getOrNull(m - 1) ?: "?"} $y"
}

/** La mention qu'un mouvement de prêt porte dans les listes ; null pour les autres types. */
internal fun loanTxMeta(type: String): String? = when (type) {
    LoanTx.LOAN_GIVEN -> "Prêt accordé"
    LoanTx.LOAN_RECEIVED -> "Prêt reçu"
    LoanTx.REPAYMENT_RECEIVED -> "Remboursement reçu"
    LoanTx.REPAYMENT_PAID -> "Remboursement versé"
    else -> null
}
